<template>
	<div class="p-0 md:p-10">
		<div class="contained-section w-full bg-gray-100 md:rounded-md md:p-10">
			<h3 class="mb-8 text-2xl">Register your interest</h3>
			<form @submit.prevent="onSubmit">
				<div class="space-y-4">
					<div class="flex flex-col gap-4 md:flex-row">
						<InputField
							v-model="firstname"
							label="First name"
							name="firstname"
							class="w-full"
							:error="errors.firstname"
						/>
						<InputField
							v-model="lastname"
							label="Last name"
							name="lastname"
							class="w-full"
							:error="errors.lastname"
						/>
					</div>
					<InputField
						v-model="email"
						label="Email"
						name="email"
						:error="errors.email"
					/>
					<InputField
						v-model="business"
						label="Company name (optional)"
						name="business_name"
						:error="errors.business"
					/>
					<div class="flex flex-col gap-4 md:flex-row">
						<InputField
							v-model="mobile_number"
							label="Contact number"
							name="mobile_number"
							class="w-full"
							:error="errors.mobile_number"
						/>
						<InputField
							v-model="postcode"
							name="postcode"
							label="Postcode"
							class="w-full"
							:error="errors.postcode"
						/>
					</div>
					<Transition name="alert">
						<AlertBox v-if="hasError">
							<AlertMessages :messages="hasError" />
						</AlertBox>
					</Transition>
					<CarbarButton
						:is-full-width="false"
						:disabled="!meta.valid || isSubmitting"
						:show-loader="isSubmitting"
						class="mt-4"
					>
						<span class="text-white">Register my interest</span>
					</CarbarButton>
				</div>
			</form>
			<div class="mt-4 text-sm text-gray-575">
				By clicking the "Register my interest" button you acknowledge
				that you have read and agree to abide by carbar's
				<CarbarLink
					font-size="text-sm"
					href="/privacy-policy"
					target="_blank"
				>
					<span>privacy policy</span>
				</CarbarLink>
				and
				<CarbarLink
					font-size="text-sm"
					href="/terms-and-conditions"
					target="_blank"
				>
					<span>terms and conditions</span>
				</CarbarLink>
				<span>*</span>
			</div>
		</div>
	</div>
</template>

<script setup>
import { useForm, useField } from 'vee-validate'
import * as yup from 'yup'
import CarbarButton from '@/components/buttons/CarbarButton.vue'
import InputField from '@/components/inputs/InputField.vue'
import { useBusinessRegister } from '@/stores/business-register-your-interest'
import CarbarLink from '@/components/links/CarbarLink.vue'
import AlertBox from '@/components/alerts/AlertBox.vue'
import AlertMessages from '@/components/alerts/AlertMessages.vue'

const businessRegister = useBusinessRegister()
const hasError = computed(() => businessRegister.hasErrorsEnquireForm)

const { handleSubmit, errors, isSubmitting, meta, resetForm } = useForm({
	validationSchema: yup.object({
		firstname: yup.string().required('First name is required'),
		lastname: yup.string().required('Last name is required'),
		email: yup
			.string()
			.trim()
			.email('Enter valid email')
			.required('Email is required'),
		business: yup.string(),
		mobile_number: yup
			.string()
			.matches(
				// eslint-disable-next-line security/detect-unsafe-regex
				/^(?:\+?61|0)[23478](?:[ -]?[0-9]){8}$/,
				'Must be a number registered in Australia'
			)
			.required('Contact number is required'),
		postcode: yup
			.string()
			.max(4, 'Postcode must be at most 4 digits')
			.matches(
				/^(0[289][0-9]{2})|([1-9][0-9]{3})$/,
				'Not a valid postcode'
			)
			.required('Postcode is required'),
	}),
})

const { value: firstname } = useField('firstname')
const { value: lastname } = useField('lastname')
const { value: business } = useField('business')
const { value: email } = useField('email')
const { value: mobile_number } = useField('mobile_number')
const { value: postcode } = useField('postcode')

const updateFormData = (formData, values) => {
	const updatedFormData = formData

	updatedFormData.firstname = values.firstname
	updatedFormData.lastname = values.lastname
	updatedFormData.email = values.email
	updatedFormData.phone = values.mobile_number
	updatedFormData.company = values.business
	updatedFormData.zip = values.postcode

	return updatedFormData
}

const onSubmit = handleSubmit(async (values) => {
	businessRegister.interestData = updateFormData(
		businessRegister.interestData,
		values
	)

	await businessRegister.submitInterestForm()

	if (!hasError.value) {
		resetAndClearForm()
	}
})

const resetAndClearForm = () => {
	resetForm()
}
</script>
